import request from '../../../util/SuperagentUtils';
import {
  bookingRequests,
  bookingTransactionRequests,
  buyerBookingsReportEndpoints,
  supplierBookingsReportEndpoints
} from '../../../requests/api-endpoints';

type BookingReportQueryParams = {
  accountId: string,
  locationId?: string,
  supplierName?: string,
  buyerName?: string,
  bookingNumber?: string,
  equipmentType?: string,
  frequency?: string,
  activeStatus?: string,
  page?: number,
  size?: number,
  sortDir?: string,
  sortBy?: string
};

type SuccessFailureType = () => void;

const getBookingSearchParams = (searchParams: BookingReportQueryParams, isPageable: boolean) => {

  const { locationId, supplierName, buyerName, bookingNumber, equipmentType, frequency, activeStatus, page, size, sortDir, sortBy } = searchParams;

  const params = isPageable
    ? new URLSearchParams([["page", String(page ?? 0)], ["size", String(size ?? 10)]])
    : new URLSearchParams();

  if (locationId) {
    params.append("locationId", locationId)
  }
  if (supplierName) {
    params.append("supplierName", supplierName)
  }
  if (buyerName) {
    params.append("buyerName", buyerName)
  }
  if (equipmentType && equipmentType !== "All") {
    params.append("assetTypeGroup", equipmentType)
  }
  if (activeStatus) {
    params.append("activeStatus", activeStatus)
  }
  if (frequency) {
    params.append("frequency", frequency)
  }
  if (sortBy) {
    params.append("sortBy", sortBy)
  }
  if (sortDir) {
    params.append("sortDir", sortDir)
  }
  if (bookingNumber) {
    params.append("bookingNumber", bookingNumber)
  }

  return params;
};

export const getBuyerBookings = (
  searchParams: BookingReportQueryParams,
  onSuccess: SuccessFailureType,
  onFail: SuccessFailureType
) => {

  const result = request.get(`${buyerBookingsReportEndpoints.search}?${getBookingSearchParams(searchParams, true).toString()}`);

  if (onSuccess && onFail) {
    result
      .then(onSuccess)
      .catch(onFail)
  } else {
    return result
  }
};

export const getSupplierBookings = (
  searchParams: BookingReportQueryParams,
  onSuccess: SuccessFailureType,
  onFail: SuccessFailureType
) => {

  const result = request.get(`${supplierBookingsReportEndpoints.search}?${getBookingSearchParams(searchParams, true).toString()}`);

  if (onSuccess && onFail) {
    result
      .then(onSuccess)
      .catch(onFail)
  } else {
    return result
  }
};

export const getSupplierBookingCSVDownload = (searchParams: BookingReportQueryParams) => {
  const params = getBookingSearchParams(searchParams, false);
  params.set("timezone", "UTC");
  return `${supplierBookingsReportEndpoints.report}?${params.toString()}`;
};

export const requestGetFailedTransactionsForBooking = (bookingId: string,
                                                       onSuccess?: (failedTransactions: Array<Object>) => void,
                                                       onFail?: (err: Object) => void): (Promise<Object> | void) => {
  const result = request
    .get(bookingTransactionRequests.failedBookingTransactions(bookingId));

  if(onSuccess && onFail) {
    result
      .then((resp) => onSuccess(resp.body))
      .catch((err) => onFail(err))
  } else {
    return result
  }
};

export const requestCompleteBooking = (
    data: { bookingId: String, tosDocUrl: String },
    onSuccess?: (booking: Object) => void,
    onFail?: (err: Object) => void
    ): (Promise<Object> | void) => {

  const result = request
    .post(bookingRequests.completeBooking)
    .send(data);

  if(onSuccess && onFail) {
    result
      .then(resp => onSuccess(resp.body))
      .catch(err => onFail(err))
  } else {
    return result
  }
};

export const requestRetryFailedTransactionsForBooking = (bookingId: string,
                                                         onSuccess?: () => void,
                                                         onFail?: (err: Object) => void): (Promise<Object> | void) => {
  const result = request
    .post(bookingTransactionRequests.failedBookingTransactionsRetry(bookingId));

  if(onSuccess && onFail) {
    result
      .then(onSuccess)
      .catch(onFail)
  } else {
    return result
  }
};

export const updatePaymentMethod = (bookingPayment: { bookingId: string, paymentMethodId: string }) => {
  return request
    .post(bookingRequests.updateBookingPaymentMethod)
    .send(bookingPayment);
};

export const postBookingStatusUpdate = (data: {status: string, body: {status: string, body: { bookingId: string}}}, onSuccess: SuccessFailureType, onFail: SuccessFailureType) => {
  const {status, body} = data;

  const requestURL = bookingRequests.updateBookingStatus[status as keyof typeof bookingRequests.updateBookingStatus];
  const result = request.post(requestURL).send(body);

  if (onSuccess && onFail) {
    result
      .then(onSuccess)
      .catch(onFail)
  } else {
    return result
  }
};
